// apps/web/src/pages/admin/StoriesAdminPage.tsx
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ConfidenceBadge } from '../../components/stories/ConfidenceBadge';
import { TierBadge } from '../../components/stories/TierBadge';
import { useAdminStories, useConfidenceShadowSummary } from '../../hooks/admin-stories';
import { ConfidenceShadowSummaryCard } from '../../components/admin/ConfidenceShadowPanel';
import { CONFIDENCE_LABELS } from '../../types/story';
import { format } from 'date-fns';

export function StoriesAdminPage() {
  const { data, isLoading } = useAdminStories();
  const shadowSummary = useConfidenceShadowSummary();
  const [search, setSearch] = useState('');
  const [confidence, setConfidence] = useState<string>('all');
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const rows = useMemo(() => {
    const all = data?.stories ?? [];
    return all.filter((s) => {
      if (search && !s.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (confidence !== 'all' && s.confidenceLabel !== confidence) return false;
      if (unverifiedOnly && !s.isUnverified) return false;
      return true;
    });
  }, [data, search, confidence, unverifiedOnly]);

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">Stories</h1>
        <p className="text-sm text-muted-foreground">Cluster admin. Click a row to inspect members and take action.</p>
      </header>

      <ConfidenceShadowSummaryCard summary={shadowSummary.data} isError={shadowSummary.isError} />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title…"
          className="max-w-sm"
        />
        <Select value={confidence} onValueChange={setConfidence}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Confidence" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All confidence</SelectItem>
            {CONFIDENCE_LABELS.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={unverifiedOnly} onChange={(e) => setUnverifiedOnly(e.target.checked)} />
          Unverified only
        </label>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="overflow-x-auto rounded border">
        <table className="min-w-full divide-y text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Section</th>
              <th className="px-3 py-2 text-left">Confidence</th>
              <th className="px-3 py-2 text-right">Sources</th>
              <th className="px-3 py-2 text-left">Publisher</th>
              <th className="px-3 py-2 text-left">Last updated</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((s) => (
              <tr key={s.id} className="transition-colors hover:bg-muted/30">
                <td className="px-3 py-2">
                  <Link to={`/admin/stories/${s.id}`} className="font-medium text-primary hover:underline">
                    {s.title}
                  </Link>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{s.section}</td>
                <td className="px-3 py-2"><ConfidenceBadge label={s.confidenceLabel} /></td>
                <td className="px-3 py-2 text-right tabular-nums">{s.sourceCount}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1">
                    {s.publisherBadge.name}
                    <TierBadge tier={s.publisherBadge.tier} />
                  </span>
                </td>
                <td className="px-3 py-2 tabular-nums">{format(new Date(s.latestSourceAddedAt), 'yyyy-MM-dd HH:mm')}</td>
              </tr>
            ))}
            {rows.length === 0 && !isLoading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">No stories match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
